const initializedSections = new WeakSet();
const TRANSITION_DURATION = 450;

const getSections = (scope = document) => {
  if (!scope) {
    return [];
  }

  if (scope.matches?.('.faq-sec')) {
    return [scope];
  }

  return Array.from(scope.querySelectorAll?.('.faq-sec') || []);
};

const getItems = (section) => Array.from(section.querySelectorAll('[data-faq-item]'))
  .map((item) => ({
    item,
    trigger: item.querySelector('[data-faq-trigger]'),
    panel: item.querySelector('[data-faq-panel]'),
    inner: item.querySelector('[data-faq-panel-inner]'),
  }))
  .filter(({ trigger, panel, inner }) => trigger && panel && inner);

const setAccessibilityState = ({ item, trigger, panel }, isOpen) => {
  item.classList.toggle('is-active', isOpen);
  trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  panel.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
};

const getPanelHeight = (panel) => Math.ceil(panel.getBoundingClientRect().height);

const finishTransition = (record, token, isOpen, state) => {
  const { item, panel } = record;
  let isFinished = false;

  const finish = () => {
    if (isFinished) {
      return;
    }

    isFinished = true;
    panel.removeEventListener('transitionend', onTransitionEnd);
    window.clearTimeout(timeout);

    if (token !== state.transitionTokens.get(panel)) {
      return;
    }

    panel.style.height = isOpen && item.classList.contains('is-active') ? 'auto' : '0px';
  };

  const onTransitionEnd = (event) => {
    if (event.target === panel && event.propertyName === 'height') {
      finish();
    }
  };

  const timeout = window.setTimeout(finish, TRANSITION_DURATION + 100);

  panel.addEventListener('transitionend', onTransitionEnd);
};

const setPanelState = (record, isOpen, state, shouldAnimate = true) => {
  const { item, panel, inner } = record;
  const wasOpen = item.classList.contains('is-active');

  if (wasOpen === isOpen) {
    setAccessibilityState(record, isOpen);

    return;
  }

  const token = (state.transitionTokens.get(panel) || 0) + 1;

  state.transitionTokens.set(panel, token);
  const reducedMotion = state.reducedMotionMedia.matches;
  const currentHeight = getPanelHeight(panel);

  setAccessibilityState(record, isOpen);

  if (!shouldAnimate || reducedMotion) {
    panel.style.height = isOpen ? 'auto' : '0px';

    return;
  }

  panel.style.height = `${currentHeight}px`;
  void panel.offsetHeight;

  window.requestAnimationFrame(() => {
    if (token !== state.transitionTokens.get(panel)) {
      return;
    }

    panel.style.height = `${isOpen ? inner.scrollHeight : 0}px`;
    finishTransition(record, token, isOpen, state);
  });
};

const initFaqSection = (scope = document) => {
  getSections(scope).forEach((section) => {
    if (initializedSections.has(section)) {
      return;
    }

    const items = getItems(section);

    if (!items.length) {
      return;
    }

    initializedSections.add(section);

    const state = {
      transitionTokens: new WeakMap(),
      reducedMotionMedia: window.matchMedia?.('(prefers-reduced-motion: reduce)') || { matches: false },
    };
    const initialItem = items.find(({ item }) => item.classList.contains('is-active')) || items[0];

    items.forEach((record) => {
      setAccessibilityState(record, record === initialItem);
      record.panel.style.height = record === initialItem ? 'auto' : '0px';
    });

    section.classList.add('is-ready');

    section.addEventListener('click', (event) => {
      const trigger = event.target.closest?.('[data-faq-trigger]');

      if (!trigger || !section.contains(trigger)) {
        return;
      }

      const selectedItem = trigger.closest('[data-faq-item]');
      const selectedRecord = items.find(({ item }) => item === selectedItem);

      if (!selectedRecord) {
        return;
      }

      const shouldOpen = !selectedRecord.item.classList.contains('is-active');

      items.forEach((record) => {
        if (record === selectedRecord) {
          setPanelState(record, shouldOpen, state);

          return;
        }

        setPanelState(record, false, state);
      });
    });

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(() => {
        items.forEach(({ item, panel, inner }) => {
          if (item.classList.contains('is-active') && panel.style.height !== 'auto') {
            panel.style.height = `${inner.scrollHeight}px`;
          }
        });
      });

      items.forEach(({ inner }) => resizeObserver.observe(inner));
    }
  });
};

initFaqSection();
window.initFaqSection = initFaqSection;
